import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import './TextResponses.css';

// Parse inline bold (**text**) into React nodes
const parseBold = (text: string): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  const regex = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(<strong key={match.index}>{match[1]}</strong>);
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
};

// Convert markdown text to React elements (bold, lists, paragraphs)
const renderMarkdown = (text: string): React.ReactNode[] => {
  const lines = text.split('\n');
  const blocks: { type: 'paragraph' | 'list'; lines: string[] }[] = [];

  for (const line of lines) {
    const trimmed = line.trimStart();
    if (trimmed.startsWith('- ')) {
      // List item line
      if (blocks.length > 0 && blocks[blocks.length - 1].type === 'list') {
        blocks[blocks.length - 1].lines.push(trimmed.slice(2));
      } else {
        blocks.push({ type: 'list', lines: [trimmed.slice(2)] });
      }
    } else if (trimmed === '') {
      // Blank line — acts as block separator, skip
      continue;
    } else {
      // Paragraph line
      if (blocks.length > 0 && blocks[blocks.length - 1].type === 'paragraph') {
        blocks[blocks.length - 1].lines.push(trimmed);
      } else {
        blocks.push({ type: 'paragraph', lines: [trimmed] });
      }
    }
  }

  return blocks.map((block, i) => {
    if (block.type === 'list') {
      return (
        <ul key={i}>
          {block.lines.map((item, j) => (
            <li key={j}>{parseBold(item)}</li>
          ))}
        </ul>
      );
    }
    return <p key={i}>{parseBold(block.lines.join(' '))}</p>;
  });
};

// Sanitize CSV cell values to prevent formula injection attacks
const sanitizeCsvCell = (value: string): string => {
  const dangerousChars = ['=', '+', '-', '@', '\t', '\r'];
  if (dangerousChars.some(char => value.startsWith(char))) {
    return "'" + value;
  }
  return value;
};

interface TextResponse {
  text: string;
  questionId: string;
  questionText: string | null;
  school: string;
  grade: string;
  surveyType: string;
  submittedAt: string;
}

const SURVEY_TYPE_OPTIONS_BY_ROLE: Record<string, { value: string; label: string }[]> = {
  student: [
    { value: '', label: 'All Surveys' },
    { value: 'k-2', label: 'K-2' },
    { value: '3-5', label: 'Grades 3-5' },
    { value: '6-8', label: 'Grades 6-8' },
    { value: 'high-school', label: 'High School' },
    { value: 'virtual-k8', label: 'Virtual K-8' },
    { value: 'virtual-hs', label: 'Virtual HS' },
  ],
  staff: [
    { value: '', label: 'All Surveys' },
    { value: 'staff-climate', label: 'Staff Climate' },
  ],
  parent: [
    { value: '', label: 'All Surveys' },
  ],
};

interface TextResponsesProps {
  respondentType?: string;
}

const TextResponses: React.FC<TextResponsesProps> = ({ respondentType = 'student' }) => {
  const { token } = useAuth();
  const [responses, setResponses] = useState<TextResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [surveyType, setSurveyType] = useState('');
  const [school, setSchool] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // AI Summarization
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [showSummary, setShowSummary] = useState(true);
  const [summaryMeta, setSummaryMeta] = useState<{ responseCount: number; filters: any } | null>(null);

  // Pagination
  const [offset, setOffset] = useState(0);
  const limit = 25;

  // Schools list
  const [schools, setSchools] = useState<string[]>([]);

  // Fetch schools for filter
  useEffect(() => {
    const fetchSchools = async () => {
      if (!token) return;
      try {
        const response = await fetch('/api/admin/schools', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          // API returns objects with {name, type, gradeRange}, extract names
          const schoolNames = (data.schools || []).map((s: any) =>
            typeof s === 'string' ? s : s.name
          );
          setSchools(schoolNames);
        }
      } catch (err) {
        console.error('Failed to fetch schools:', err);
      }
    };
    fetchSchools();
  }, [token]);

  const fetchResponses = useCallback(async () => {
    if (!token) return;

    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
        role: respondentType,
      });

      if (surveyType) params.append('surveyType', surveyType);
      if (school) params.append('school', school);
      if (search) params.append('search', search);

      const response = await fetch(`/api/admin/stats/text-responses?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to fetch responses');

      const data = await response.json();
      setResponses(data.responses || []);
      setTotal(data.total || 0);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [token, surveyType, school, search, offset, respondentType]);

  useEffect(() => {
    fetchResponses();
  }, [fetchResponses]);

  // Clear summary when filters change
  useEffect(() => {
    setSummary(null);
    setSummaryError(null);
    setSummaryMeta(null);
  }, [surveyType, school, search]);

  const handleSummarize = async () => {
    if (!token) return;

    setIsSummarizing(true);
    setSummaryError(null);
    setSummary(null);
    setSummaryMeta(null);

    try {
      const body: Record<string, string> = { role: respondentType };
      if (surveyType) body.surveyType = surveyType;
      if (school) body.school = school;
      if (search) body.search = search;

      const response = await fetch('/api/admin/summarize-text-responses', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate summary');
      }

      setSummary(data.summary);
      setSummaryMeta({ responseCount: data.responseCount, filters: data.filters });
      setShowSummary(true);
    } catch (err: any) {
      setSummaryError(err.message);
    } finally {
      setIsSummarizing(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setOffset(0);
  };

  const handleExport = async () => {
    if (!token) return;

    try {
      const params = new URLSearchParams({ limit: '10000', offset: '0', role: respondentType });
      if (surveyType) params.append('surveyType', surveyType);
      if (school) params.append('school', school);
      if (search) params.append('search', search);

      const response = await fetch(`/api/admin/stats/text-responses?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Export failed');

      const data = await response.json();
      const csvRows = [
        ['Response', 'Question', 'School', 'Grade', 'Survey Type', 'Submitted'].join(','),
        ...data.responses.map((r: TextResponse) =>
          [
            `"${sanitizeCsvCell(r.text).replace(/"/g, '""')}"`,
            `"${sanitizeCsvCell(r.questionText || r.questionId).replace(/"/g, '""')}"`,
            `"${sanitizeCsvCell(r.school).replace(/"/g, '""')}"`,
            `"${sanitizeCsvCell(r.grade).replace(/"/g, '""')}"`,
            `"${sanitizeCsvCell(r.surveyType).replace(/"/g, '""')}"`,
            `"${new Date(r.submittedAt).toLocaleDateString()}"`,
          ].join(',')
        ),
      ];

      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `text-responses-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      alert('Export failed: ' + err.message);
    }
  };

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <div className="text-responses">
      <div className="text-responses__header">
        <div>
          <h2>Open-Ended Responses</h2>
          <p className="text-responses__description">
            Browse and analyze {respondentType} feedback from text questions
          </p>
        </div>
        <div className="text-responses__actions">
          <button
            className="text-responses__summarize-btn"
            onClick={handleSummarize}
            disabled={isSummarizing || total === 0}
          >
            {isSummarizing ? 'Summarizing...' : 'Summarize'}
          </button>
          <button className="text-responses__export-btn" onClick={handleExport}>
            Export CSV
          </button>
        </div>
      </div>

      <div className="text-responses__filters">
        <div className="text-responses__filter-row">
          <select
            value={surveyType}
            onChange={(e) => {
              setSurveyType(e.target.value);
              setOffset(0);
            }}
            className="text-responses__select"
          >
            {(SURVEY_TYPE_OPTIONS_BY_ROLE[respondentType] || SURVEY_TYPE_OPTIONS_BY_ROLE.student).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          <select
            value={school}
            onChange={(e) => {
              setSchool(e.target.value);
              setOffset(0);
            }}
            className="text-responses__select"
          >
            <option value="">All Schools</option>
            {schools.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>

          <form onSubmit={handleSearch} className="text-responses__search">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search responses..."
              className="text-responses__search-input"
            />
            <button type="submit" className="text-responses__search-btn">
              Search
            </button>
          </form>
        </div>

        <div className="text-responses__stats">
          <span>{total} total responses</span>
          {search && <span className="text-responses__active-filter">Searching: "{search}"</span>}
        </div>
      </div>

      {summaryError && (
        <div className="text-responses__summary-error">
          <p>Summarization failed: {summaryError}</p>
        </div>
      )}

      {isSummarizing && (
        <div className="text-responses__summary-loading">
          <div className="text-responses__spinner" />
          <p>Summarizing Responses...</p>
        </div>
      )}

      {summary && showSummary && !isSummarizing && (
        <div className="text-responses__summary">
          <div className="text-responses__summary-header">
            <div>
              <h3>Summary of Responses</h3>
              {summaryMeta && (
                <span className="text-responses__summary-meta">
                  Based on {summaryMeta.responseCount} responses
                </span>
              )}
            </div>
            <div className="text-responses__summary-actions">
              <button
                className="text-responses__summary-toggle"
                onClick={() => setShowSummary(false)}
              >
                Hide
              </button>
              <button
                className="text-responses__summary-dismiss"
                onClick={() => {
                  setSummary(null);
                  setSummaryMeta(null);
                }}
              >
                Dismiss
              </button>
            </div>
          </div>
          <div className="text-responses__summary-content">
            {renderMarkdown(summary)}
          </div>
        </div>
      )}

      {summary && !showSummary && !isSummarizing && (
        <div className="text-responses__summary text-responses__summary--collapsed">
          <div className="text-responses__summary-header">
            <h3>Summary of Responses</h3>
            <button
              className="text-responses__summary-toggle"
              onClick={() => setShowSummary(true)}
            >
              Show
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="text-responses__loading">
          <div className="text-responses__spinner" />
          <p>Loading responses...</p>
        </div>
      ) : error ? (
        <div className="text-responses__error">
          <p>Error: {error}</p>
        </div>
      ) : responses.length === 0 ? (
        <div className="text-responses__empty">
          <p>No text responses found matching your filters.</p>
        </div>
      ) : (
        <>
          <div className="text-responses__list">
            {responses.map((response, index) => (
              <div key={`${response.questionId}-${index}`} className="text-responses__item">
                <div className="text-responses__item-content">
                  <p className="text-responses__item-text">"{response.text}"</p>
                  <div className="text-responses__item-meta">
                    <span className="text-responses__item-question">
                      {response.questionText || response.questionId}
                    </span>
                  </div>
                </div>
                <div className="text-responses__item-details">
                  <span className="detail-tag">{response.school}</span>
                  <span className="detail-tag">{response.grade}</span>
                  <span className="detail-tag">{response.surveyType}</span>
                  <span className="detail-date">
                    {new Date(response.submittedAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="text-responses__pagination">
              <button
                className="pagination-btn"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - limit))}
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="pagination-btn"
                disabled={offset + limit >= total}
                onClick={() => setOffset(offset + limit)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TextResponses;
